import { Component } from '@theme/component';
import { WishlistStore, WishlistUpdateEvent } from '@theme/wishlist';
import { CartAddEvent } from '@theme/events';
import { cartDisplayCount } from '@theme/cart-count';

const PRODUCT_CARD_SECTION_ID = 'section-rendering-product-card';
// Wishlist sits inside the PDP card shell, which is narrower than the full
// collection page — so we use a smaller min-width than collection (200px vs
// 250px) to keep ~3 cards per row on desktop while staying close to the
// collection card visual scale. 16px gaps match collection.
const PDP_RESOURCE_LIST_GRID_STYLE =
  '--resource-list-column-gap-desktop: 16px;' +
  '--resource-list-row-gap-desktop: 16px;' +
  '--resource-list-columns: repeat(auto-fill, minmax(200px, 1fr));' +
  '--resource-list-columns-mobile: repeat(2, 1fr);' +
  '--column-count-mobile: 2;';

// Inline icons for the manage dropdown (this asset can't use Liquid's
// inline_asset_content, so the markup is embedded here).
const ICONS = {
  star:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9z"/></svg>',
  clear:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/></svg>',
  trash:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/></svg>',
};

/**
 * @typedef {{ handle: string }} WishlistItem
 * @typedef {{ id: string, name: string, isDefault?: boolean, items: WishlistItem[] }} WishlistList
 * @typedef {{ lists: WishlistList[] }} WishlistState
 */

class WishlistManagerComponent extends Component {
  requiredRefs = ['listNav', 'listGrid', 'emptyMessage'];

  /** @type {boolean} */
  #addingAll = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #statusTimer = null;
  /** @type {string} Id of the currently displayed list — read by the card share button + sort. */
  #activeListId = 'default';

  /** @returns {HTMLElement} */
  #getListNav() {
    return /** @type {HTMLElement} */ (this.refs.listNav);
  }

  /** @returns {HTMLElement} */
  #getListGrid() {
    return /** @type {HTMLElement} */ (this.refs.listGrid);
  }

  /** @returns {HTMLElement} */
  #getEmptyMessage() {
    return /** @type {HTMLElement} */ (this.refs.emptyMessage);
  }

  /** @returns {HTMLButtonElement | null} */
  #getAddAllBtn() {
    return /** @type {HTMLButtonElement | null} */ (this.refs.addAllBtn ?? null);
  }

  /** @returns {HTMLElement | null} */
  #getAddAllStatus() {
    return /** @type {HTMLElement | null} */ (this.refs.addAllStatus ?? null);
  }

  /** @returns {HTMLElement | null} */
  #getCardName() {
    return /** @type {HTMLElement | null} */ (this.refs.cardName ?? null);
  }

  /** @returns {HTMLElement | null} */
  #getCardCount() {
    return /** @type {HTMLElement | null} */ (this.refs.cardCount ?? null);
  }

  /** @returns {HTMLButtonElement | null} */
  #getShareBtn() {
    return /** @type {HTMLButtonElement | null} */ (this.refs.shareBtn ?? null);
  }

  /** @returns {HTMLElement | null} */
  #getToolbar() {
    return /** @type {HTMLElement | null} */ (this.refs.toolbar ?? null);
  }

  /** @returns {HTMLSelectElement | null} */
  #getSortSelect() {
    return /** @type {HTMLSelectElement | null} */ (this.refs.sortSelect ?? null);
  }

  #getSort() {
    return sessionStorage.getItem('wishlist-sort') || 'recent';
  }

  /** @returns {HTMLFormElement | null} */
  #getNewListMenu() {
    return this.querySelector('[data-new-list-menu]');
  }

  /** @returns {HTMLInputElement | null} */
  #getNewListInput() {
    return this.querySelector('[data-new-list-input]');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
    // The add-all / share / sort controls live in the static section markup
    // (not rebuilt by #render), so bind once here to avoid stacking listeners.
    this.#getAddAllBtn()?.addEventListener('click', this.#onAddAllClick);
    this.#getShareBtn()?.addEventListener('click', this.#onShareClick);
    const sortSelect = this.#getSortSelect();
    if (sortSelect) {
      sortSelect.value = this.#getSort();
      sortSelect.addEventListener('change', this.#onSortChange);
    }
    this.#render();
  }

  #onShareClick = () => this.#openShareModal(this.#activeListId);

  #onSortChange = () => {
    const select = this.#getSortSelect();
    if (select) sessionStorage.setItem('wishlist-sort', select.value);
    this.#render();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#onUpdate);
    document.removeEventListener('click', this.#onDocumentClick);
    this.#getAddAllBtn()?.removeEventListener('click', this.#onAddAllClick);
    this.#getShareBtn()?.removeEventListener('click', this.#onShareClick);
    this.#getSortSelect()?.removeEventListener('change', this.#onSortChange);
    if (this.#statusTimer) clearTimeout(this.#statusTimer);
  }

  #onUpdate = () => this.#render();

  #render() {
    const state = /** @type {WishlistState} */ (WishlistStore.getLists());
    const activeListId = this.#getActiveListId() || state.lists[0]?.id || 'default';
    const activeList = state.lists.find((l) => l.id === activeListId);
    const canDelete = state.lists.length > 1;
    const listNav = this.#getListNav();
    const listGrid = this.#getListGrid();
    const emptyMessage = this.#getEmptyMessage();

    this.#activeListId = activeListId;

    const itemCount = activeList?.items.length ?? 0;
    const hasItems = itemCount > 0;
    const addAllBtn = this.#getAddAllBtn();
    if (addAllBtn) addAllBtn.hidden = !hasItems;
    const toolbar = this.#getToolbar();
    if (toolbar) toolbar.hidden = !hasItems;
    // A list switch / change invalidates any prior "added N" status note.
    if (!this.#addingAll) this.#clearAddAllStatus();

    // Summary card reflects the active list.
    const cardName = this.#getCardName();
    if (cardName) cardName.textContent = activeList?.name ?? '';
    const cardCount = this.#getCardCount();
    if (cardCount) cardCount.textContent = String(itemCount);

    const activeIsDefault = activeList?.isDefault ?? false;

    listNav.innerHTML = `
      <div class="wishlist__chips-row">
        ${state.lists.map((l) => `
          <button class="wishlist__chip${l.id === activeListId ? ' is-active' : ''}" data-list-id="${l.id}">
            <span class="wishlist__chip-name">${escapeHtml(l.name)}${l.isDefault ? ' <span class="wishlist__chip-star" aria-hidden="true">★</span>' : ''}</span>
            <span class="wishlist__chip-count">${l.items.length}</span>
          </button>
        `).join('')}
        <div class="wishlist__new-list-wrap" data-new-list-wrap>
          <button type="button" class="wishlist__chip wishlist__chip--new" data-new-list-toggle>
            <span class="wishlist__chip-plus" aria-hidden="true">+</span>
            ${Theme.translations?.new_list ?? 'Create new list'}
          </button>
          <form class="wishlist__new-list-menu" data-new-list-menu>
            <input
              type="text"
              class="wishlist__new-list-input"
              data-new-list-input
              placeholder="${Theme.translations?.add_new_list ?? 'New list name'}"
              maxlength="40"
              autocomplete="off"
              aria-label="${Theme.translations?.add_new_list ?? 'New list name'}"
            >
            <div class="wishlist__new-list-actions">
              <button type="submit" class="wishlist__new-list-action wishlist__new-list-action--primary">${Theme.translations?.create ?? 'Create'}</button>
              <button type="button" class="wishlist__new-list-action" data-new-list-cancel>${Theme.translations?.cancel ?? 'Cancel'}</button>
            </div>
          </form>
        </div>
        <div class="wishlist__manage-wrap">
          <button class="wishlist__chip wishlist__chip--manage" data-manage-btn="${activeListId}" aria-label="Manage list" aria-haspopup="true">⋯</button>
          <div class="wishlist__manage-dropdown" data-manage-dropdown="${activeListId}">
            ${!activeIsDefault ? `<button class="wishlist__manage-dropdown-item" data-set-default="${activeListId}">${ICONS.star}<span>Set as default</span></button>` : ''}
            <button class="wishlist__manage-dropdown-item" data-clear-list="${activeListId}">${ICONS.clear}<span>Clear list</span></button>
            ${canDelete ? `<button class="wishlist__manage-dropdown-item wishlist__manage-dropdown-item--danger" data-delete-list="${activeListId}">${ICONS.trash}<span>Delete list</span></button>` : ''}
          </div>
        </div>
      </div>
    `;

    // Always attach listeners so nav is always clickable
    this.#attachEventListeners(activeListId);

    if (!activeList || activeList.items.length === 0) {
      listGrid.innerHTML = '';
      emptyMessage.classList.remove('hidden');
      return;
    }

    emptyMessage.classList.add('hidden');
    this.#renderGrid(activeList);
  }

  /**
   * @param {WishlistList} list
   */
  async #renderGrid(list) {
    const sort = this.#getSort();

    // addedAt-based sorts are resolved up front so the fetch order matches the
    // final order; price/name sorts need the rendered card and are applied after.
    const items = [...list.items];
    if (sort === 'recent') items.sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0));
    else if (sort === 'oldest') items.sort((a, b) => (a.addedAt ?? 0) - (b.addedAt ?? 0));

    const cards = await Promise.all(items.map((item) => this.#fetchProductCard(item.handle)));

    let entries = items
      .map((item, i) => ({ item, html: cards[i] }))
      .filter((e) => e.html);

    if (sort === 'price-asc' || sort === 'price-desc' || sort === 'name-asc') {
      entries.forEach((e) => {
        const meta = parseCardMeta(e.html);
        e.price = meta.price;
        e.title = meta.title;
      });
      if (sort === 'name-asc') {
        entries.sort((a, b) => a.title.localeCompare(b.title));
      } else {
        const dir = sort === 'price-asc' ? 1 : -1;
        entries.sort((a, b) => (a.price - b.price) * dir);
      }
    }

    const gridItems = entries
      .map((e) => `<div class="resource-list__item">${e.html}</div>`)
      .join('');

    this.#getListGrid().innerHTML = `<div class="resource-list resource-list--grid" style="${PDP_RESOURCE_LIST_GRID_STYLE}">${gridItems}</div>`;
  }

  /**
   * Adds every purchasable item in the active list to the cart in a single
   * request. Variant ids are read from the already-rendered product cards —
   * each card's quick-add form carries a hidden `input[name="id"]` holding the
   * product's selected-or-first-available variant, disabled when it can't be
   * added (sold out / unavailable). Multi-option products contribute their
   * default variant; items needing an explicit choice or that are sold out are
   * skipped and reported.
   */
  #onAddAllClick = async () => {
    if (this.#addingAll) return;

    const grid = this.#getListGrid();
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      grid.querySelectorAll('product-card input[name="id"]:not([disabled])')
    );

    /** @type {Map<string, number>} */
    const quantities = new Map();
    if (inputs.length > 0) {
      for (const input of inputs) {
        const variantId = input.value;
        if (!variantId) continue;
        const qtyInput = input.form?.querySelector('input[name="quantity"]');
        const qty = Math.max(1, parseInt(qtyInput?.value ?? '1', 10) || 1);
        quantities.set(variantId, (quantities.get(variantId) ?? 0) + qty);
      }
    } else {
      // Quick-add is disabled theme-wide (settings.quick_add / mobile_quick_add
      // off), so cards carry no add-to-cart form. Fall back to the variant JSON
      // every card always embeds (<variant-picker><script>…</script>) to resolve
      // each product's default purchasable variant. Sold-out variants are skipped.
      for (const card of grid.querySelectorAll('product-card')) {
        const json = card.querySelector('variant-picker script[type="application/json"]')?.textContent;
        if (!json) continue;
        try {
          const variant = JSON.parse(json);
          if (!variant?.id || variant.available === false) continue;
          const variantId = String(variant.id);
          quantities.set(variantId, (quantities.get(variantId) ?? 0) + 1);
        } catch {
          // unparseable card JSON — skip it
        }
      }
    }

    const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity }));
    const totalAddedQty = items.reduce((sum, i) => sum + i.quantity, 0);
    const totalInList = this.#getListGrid().querySelectorAll('product-card').length;

    const btn = this.#getAddAllBtn();
    // Label lives in a <span> alongside the cart icon — update only the text so
    // the icon survives the adding/restore cycle.
    const labelEl = btn?.querySelector('span') ?? btn;
    const setBtnLabel = (txt) => { if (labelEl) labelEl.textContent = txt; };
    const defaultLabel = btn?.dataset.addAllDefaultLabel ?? labelEl?.textContent?.trim() ?? 'Add all to cart';
    const addingLabel = btn?.dataset.addAllAddingLabel ?? 'Adding…';
    const successLabel = btn?.dataset.addAllSuccessLabel ?? 'Added to cart';
    const noneLabel = btn?.dataset.addAllNoneLabel ?? 'Nothing could be added to cart';

    if (items.length === 0) {
      this.#setAddAllStatus(noneLabel);
      return;
    }

    // Stays true on a successful add so the finally block leaves the button in
    // its disabled "Added to cart" success state instead of restoring it.
    let succeeded = false;
    this.#addingAll = true;
    if (btn) {
      btn.disabled = true;
      setBtnLabel(addingLabel);
    }
    this.#clearAddAllStatus();

    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
        .filter(Boolean);

      const resp = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await resp.json();

      if (!resp.ok || data.status) {
        this.#setAddAllStatus(data?.message ?? noneLabel);
        return;
      }

      // Authoritative cart total so the bubble reflects the real count. When
      // we have it, dispatch an absolute count (source !== product-form, which
      // the cart icon treats as a set). If /cart.js fails, fall back to the
      // product-form semantics where itemCount is the delta the icon adds on.
      let cart = null;
      try {
        cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      } catch {
        // fall back to delta-increment below
      }

      document.dispatchEvent(
        cart
          ? new CartAddEvent(cart, '', {
              source: 'wishlist-add-all',
              // Not `item_count` — the bubble counts a bundle once. See cart-count.js.
              itemCount: cartDisplayCount(cart),
              sections: data.sections,
            })
          : new CartAddEvent(data, '', {
              source: 'product-form-component',
              itemCount: totalAddedQty,
              sections: data.sections,
            })
      );

      succeeded = true;

      const addedCount = items.length;
      if (addedCount < totalInList) {
        const tpl = btn?.dataset.addAllSkippedTpl
          ?? 'Added __COUNT__ to cart — some items need options or are sold out';
        this.#setAddAllStatus(tpl.replace('__COUNT__', String(addedCount)));
      }
    } catch {
      this.#setAddAllStatus(noneLabel);
    } finally {
      this.#addingAll = false;
      if (btn) {
        if (succeeded) {
          // Lock the button in its success state — green, "Added to cart", and
          // disabled so the same items can't be piled on with repeat clicks.
          setBtnLabel(successLabel);
          btn.classList.add('is-success');
          btn.disabled = true;
        } else {
          btn.disabled = false;
          setBtnLabel(defaultLabel);
        }
      }
    }
  };

  /**
   * @param {string} message
   */
  #setAddAllStatus(message) {
    const status = this.#getAddAllStatus();
    if (!status) return;
    status.textContent = message;
    if (this.#statusTimer) clearTimeout(this.#statusTimer);
    this.#statusTimer = setTimeout(() => this.#clearAddAllStatus(), 6000);
  }

  #clearAddAllStatus() {
    const status = this.#getAddAllStatus();
    if (status) status.textContent = '';
    if (this.#statusTimer) {
      clearTimeout(this.#statusTimer);
      this.#statusTimer = null;
    }
  }

  /**
   * @param {string} handle
   */
  async #fetchProductCard(handle) {
    try {
      const cardUrl = `/products/${encodeURIComponent(handle)}?section_id=${PRODUCT_CARD_SECTION_ID}`;
      const resp = await fetch(cardUrl);
      if (!resp.ok) {
        console.warn(`Failed to fetch product ${handle}: ${resp.status}`);
        return null;
      }
      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cardRoot = doc.querySelector('[data-wishlist-product-card-root]');
      if (!cardRoot) return null;
      return cardRoot.innerHTML.trim();
    } catch (err) {
      console.error(`Error fetching product ${handle}:`, err);
      return null;
    }
  }

  #getActiveListId() {
    return sessionStorage.getItem('wishlist-active-list-id');
  }

  /**
   * @param {string} id
   */
  #setActiveListId(id) {
    sessionStorage.setItem('wishlist-active-list-id', id);
  }

  /**
   * @param {MouseEvent} e
   */
  #onDocumentClick = (e) => {
    const target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
    if (!target) return;

    if (!target.closest('[data-manage-btn]') && !target.closest('[data-manage-dropdown]')) {
      this.#getListNav().querySelectorAll('[data-manage-dropdown]').forEach((d) => d.classList.remove('is-open'));
    }

    if (!target.closest('[data-new-list-wrap]')) {
      this.#getNewListMenu()?.classList.remove('is-open');
      this.#getNewListInput()?.blur();
    }
  };

  /**
   * @param {string} activeListId
   */
  #attachEventListeners(activeListId) {
    const listNav = this.#getListNav();

    // List switching
    listNav.querySelectorAll('[data-list-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const listId = btn.getAttribute('data-list-id');
        if (!listId) return;
        this.#setActiveListId(listId);
        this.#render();
      });
    });

    // New list
    this.querySelector('[data-new-list-toggle]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      const menu = this.#getNewListMenu();
      const input = this.#getNewListInput();
      const isOpen = menu?.classList.contains('is-open');

      menu?.classList.toggle('is-open', !isOpen);
      if (!isOpen) {
        input?.focus();
      } else {
        input?.blur();
      }
    });

    this.#getNewListMenu()?.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    this.#getNewListMenu()?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = this.#getNewListInput();
      const name = input?.value?.trim();
      if (!name) return;

      const createdListId = WishlistStore.createList(name);
      if (createdListId) this.#setActiveListId(createdListId);

      if (input) input.value = '';
      this.#getNewListMenu()?.classList.remove('is-open');
      this.#render();
    });

    this.querySelector('[data-new-list-cancel]')?.addEventListener('click', () => {
      const menu = this.#getNewListMenu();
      const input = this.#getNewListInput();
      menu?.classList.remove('is-open');
      if (input) input.value = '';
      input?.blur();
    });

    this.querySelector('[data-new-list-input]')?.addEventListener('keydown', (event) => {
      const keyboardEvent = /** @type {KeyboardEvent} */ (event);
      if (keyboardEvent.key !== 'Escape') return;
      const input = this.#getNewListInput();
      const menu = this.#getNewListMenu();
      menu?.classList.remove('is-open');
      if (input) input.value = '';
      input?.blur();
    });

    // Manage menu toggle
    listNav.querySelectorAll('[data-manage-btn]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const listId = btn.getAttribute('data-manage-btn');
        if (!listId) return;
        const dropdown = listNav.querySelector(`[data-manage-dropdown="${listId}"]`);
        const isOpen = dropdown?.classList.contains('is-open');
        // Close all first
        listNav.querySelectorAll('[data-manage-dropdown]').forEach((d) => d.classList.remove('is-open'));
        if (!isOpen) dropdown?.classList.add('is-open');
      });
    });

    // Set as default
    listNav.querySelectorAll('[data-set-default]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const listId = btn.getAttribute('data-set-default');
        if (!listId) return;
        WishlistStore.setDefault(listId);
      });
    });

    // Clear list
    listNav.querySelectorAll('[data-clear-list]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const listId = btn.getAttribute('data-clear-list');
        if (!listId) return;
        WishlistStore.clearList(listId);
      });
    });

    // Delete list
    listNav.querySelectorAll('[data-delete-list]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const listId = btn.getAttribute('data-delete-list');
        if (!listId) return;
        WishlistStore.deleteList(listId);
        // If we deleted the active list, switch to first remaining
        if (listId === activeListId) {
          const remaining = WishlistStore.getLists().lists;
          this.#setActiveListId(remaining[0]?.id ?? 'default');
        }
      });
    });

    // Close dropdowns on outside click
    document.removeEventListener('click', this.#onDocumentClick);
    document.addEventListener('click', this.#onDocumentClick);
  }

  /**
   * Share modal for a single list. Opt-in: the link is only minted when the
   * user clicks "Create share link" (not when the modal opens), and only this
   * one list is ever exposed.
   * @param {string} listId
   */
  #openShareModal(listId) {
    const list = WishlistStore.getLists().lists.find((l) => l.id === listId);
    if (!list) return;

    if (!Theme.customerId) {
      window.location.href = `/customer_authentication/login?return_to=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'wishlist-share-backdrop';
    document.body.appendChild(backdrop);
    document.body.style.overflow = 'hidden';

    const close = () => {
      backdrop.remove();
      document.body.style.overflow = '';
    };
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
    document.addEventListener('keydown', function onKey(e) {
      if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onKey); }
    });

    const renderUnshared = () => {
      backdrop.innerHTML = `
        <div class="wishlist-share-modal" role="dialog" aria-modal="true" aria-label="Share list">
          <button type="button" class="wishlist-share-modal__close" data-share-close aria-label="Close">&times;</button>
          <h2 class="wishlist-share-modal__title">Share &ldquo;${escapeHtml(list.name)}&rdquo;</h2>
          <p class="wishlist-share-modal__sub">Create a private link anyone can open to view this list. Only this list is shared &mdash; your other lists stay private, and you can stop sharing anytime.</p>
          <div class="wishlist-share-modal__actions">
            <button type="button" class="wishlist-share-btn wishlist-share-btn--primary" data-share-create>Create share link</button>
          </div>
        </div>`;
      backdrop.querySelector('[data-share-close]')?.addEventListener('click', close);
      backdrop.querySelector('[data-share-create]')?.addEventListener('click', async (e) => {
        const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
        btn.disabled = true;
        btn.textContent = 'Creating…';
        const slug = await WishlistStore.share(listId);
        if (!slug) {
          btn.disabled = false;
          btn.textContent = 'Create share link';
          return;
        }
        renderShared(slug);
      });
    };

    const renderShared = (slug) => {
      const url = WishlistStore.shareUrl(slug);
      backdrop.innerHTML = `
        <div class="wishlist-share-modal" role="dialog" aria-modal="true" aria-label="Share list">
          <button type="button" class="wishlist-share-modal__close" data-share-close aria-label="Close">&times;</button>
          <h2 class="wishlist-share-modal__title">Share &ldquo;${escapeHtml(list.name)}&rdquo;</h2>
          <p class="wishlist-share-modal__sub">Anyone with this link can view this list.</p>
          <div class="wishlist-share-link">
            <span class="wishlist-share-link__url">${escapeHtml(url)}</span>
            <button type="button" class="wishlist-share-btn wishlist-share-btn--primary" data-share-copy>Copy</button>
          </div>
          <div class="wishlist-share-qr" data-share-qr aria-hidden="true"></div>
          <div class="wishlist-share-methods">
            <a class="wishlist-share-method" href="mailto:?subject=${encodeURIComponent(list.name)}&body=${encodeURIComponent('Take a look at my wishlist: ' + url)}">Email</a>
            <a class="wishlist-share-method" href="sms:?&body=${encodeURIComponent('My wishlist: ' + url)}">Text</a>
            <a class="wishlist-share-method" href="https://wa.me/?text=${encodeURIComponent('My wishlist: ' + url)}" target="_blank" rel="noopener">WhatsApp</a>
            <a class="wishlist-share-method" href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}" target="_blank" rel="noopener">Facebook</a>
          </div>
          <button type="button" class="wishlist-share-stop" data-share-stop>Stop sharing</button>
        </div>`;
      backdrop.querySelector('[data-share-close]')?.addEventListener('click', close);

      const copyBtn = backdrop.querySelector('[data-share-copy]');
      copyBtn?.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(url);
          copyBtn.textContent = 'Copied!';
          setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1600);
        } catch {
          copyBtn.textContent = 'Copy failed';
          setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1600);
        }
      });

      backdrop.querySelector('[data-share-stop]')?.addEventListener('click', async (e) => {
        const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
        btn.disabled = true;
        btn.textContent = 'Removing…';
        const ok = await WishlistStore.unshare(listId);
        if (ok) renderUnshared();
        else {
          btn.disabled = false;
          btn.textContent = 'Stop sharing';
        }
      });

      // QR code — loaded from the dynamic Shopify CDN asset URL handed in via
      // the host element's data-qr-src (Liquid can't be used inside this asset).
      const qrSrc = this.dataset.qrSrc;
      const qrHost = backdrop.querySelector('[data-share-qr]');
      if (qrSrc && qrHost) {
        import(qrSrc)
          .then(({ QRCode }) => {
            new QRCode(qrHost, {
              text: url,
              width: 132,
              height: 132,
              colorDark: '#1f1f1f',
              colorLight: '#ffffff',
              correctLevel: 0,
            });
          })
          .catch(() => { /* QR is a nicety — link still works without it */ });
      }
    };

    if (list.shareSlug) renderShared(list.shareSlug);
    else renderUnshared();
  }
}

/**
 * Pull a sortable title + numeric price out of a rendered product-card's HTML.
 * Price is read from the `<product-price>` text (first money value, in major
 * units); title from `<product-title>` / the card heading. Missing values sort
 * last for price and as an empty string for name.
 * @param {string} html
 * @returns {{ price: number, title: string }}
 */
function parseCardMeta(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const title = (
    doc.querySelector('product-title')?.textContent ||
    doc.querySelector('h3')?.textContent ||
    ''
  ).trim();

  const priceText = doc.querySelector('product-price')?.textContent ?? '';
  // Grab the first "1,234.56" / "1234" run, strip thousands separators.
  const match = priceText.replace(/[^\d.,]/g, ' ').match(/\d[\d.,]*/);
  let price = Number.POSITIVE_INFINITY;
  if (match) {
    const normalized = match[0].replace(/,/g, '');
    const parsed = parseFloat(normalized);
    if (!Number.isNaN(parsed)) price = parsed;
  }
  return { price, title };
}

/**
 * @param {unknown} s
 * @returns {string}
 */
function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('wishlist-manager-component')) {
  customElements.define('wishlist-manager-component', WishlistManagerComponent);
}
